/**
 * syncEngine.ts — live-document auto-save watcher.
 *
 * HISTORY: this module used to mirror every paragraph edit to the standalone
 * "DocService" block store as an OOXML DLL. DocService was decommissioned
 * (its formatting store is redundant with the Save-gated ``section_ooxml``
 * snapshots that the download rebuild composes). This module was rewritten to
 * REUSE the same event/debounce/pause machinery to drive AUTO-SAVE instead:
 * whenever the user edits the live doc, the affected section's markdown
 * (``draft_full``) + OOXML (``section_ooxml``) are persisted straight to the
 * Mongo SSOT — the same write the Save button performs, scoped to one section.
 *
 * Public API (consumed by VariantCShell / useSyncStatus / useDraftFlow /
 * wordWriter — keep stable):
 *   - initSyncEngine(getRunId) → cleanup    start/stop the watcher
 *   - pauseSync() / resumeSync(runId?)       suspend around programmatic writes
 *   - subscribeSyncStatus(cb) / isSyncing()  "saving…" indicator state
 *   - _isLikelyLoadingPlaceholderImage(...)   placeholder-image guard (wordWriter)
 *
 * Section-scoping: the watcher has no paragraph→section map of its own. On each
 * settled edit it resolves the CURSOR's owning section via the ``ASTHRA_SEC_*``
 * bookmarks (``detectCursorSectionLight``) and saves that section (plus any it
 * accumulated from earlier triggers in the same window). The section-scoped
 * save hash-compares against the SSOT, so a redundant save is a cheap no-op.
 */

// ---------------------------------------------------------------------------
// Placeholder-image guard (also imported by wordWriter — keep exported)
// ---------------------------------------------------------------------------

// A freshly-inserted inline picture that Word-for-the-web hasn't finished
// loading reads back through getBase64ImageSrc() as a tiny "loading" BITMAP
// (~850-byte image/bmp), NOT the real figure bytes. Capturing that into a
// section_ooxml snapshot would bake a grey box into the saved formatting, so
// the OOXML capture DEFERS while a placeholder is detected (see wordWriter's
// readSectionSubtreeOoxml). Bounded so a genuine (rare) small BMP can't defer
// forever.
const _PLACEHOLDER_IMAGE_MAX_BYTES = 2048; // real CSR figures are JPEG/PNG, ≫2 KB

/** Decoded byte length of a base64 payload (no data-URI prefix). */
export function _base64ByteLength(base64: string): number {
  const len = base64.length;
  if (len === 0) return 0;
  const pad = base64.endsWith("==") ? 2 : base64.endsWith("=") ? 1 : 0;
  return Math.floor((len * 3) / 4) - pad;
}

/** True when captured inline-picture bytes look like Word-for-the-web's
 *  still-loading placeholder bitmap (a tiny BMP) rather than a real figure. */
export function _isLikelyLoadingPlaceholderImage(base64: string, format: string): boolean {
  return format === "bmp" && _base64ByteLength(base64) > 0 && _base64ByteLength(base64) <= _PLACEHOLDER_IMAGE_MAX_BYTES;
}

// ---------------------------------------------------------------------------
// Timing constants
// ---------------------------------------------------------------------------

// Idle window after the last edit trigger before an auto-save fires. Matches
// the legacy sync debounce so typing feels identical.
const DEBOUNCE_MS = 1500;

// While WordApi 1.6 paragraph events are active, caret-only selection changes
// are throttled to this heartbeat — real content edits already fire paragraph
// events, but the heartbeat still backstops toolbar formatting changes that
// mutate a paragraph without a content event.
const SELECTION_HEARTBEAT_MS = 15000;

type SyncTrigger = "paragraph" | "selection";

// ---------------------------------------------------------------------------
// Module state
// ---------------------------------------------------------------------------

let _paused = false;
let _pendingTimer: ReturnType<typeof setTimeout> | null = null;
let _lastSelectionArmAt = 0;

// Sections touched since the last completed save (dotted numbers, e.g. "9.2").
const _touchedSections = new Set<string>();

// "saving in flight" flag + subscribers (drives the top-bar Syncing/Synced pill).
let _syncing = false;
const _statusSubscribers = new Set<(syncing: boolean) => void>();

function _setSyncing(next: boolean): void {
  if (_syncing === next) return;
  _syncing = next;
  for (const cb of _statusSubscribers) {
    try {
      cb(next);
    } catch {
      /* a subscriber throwing must never break the save loop */
    }
  }
}

function _nowMs(): number {
  return Date.now();
}

// ---------------------------------------------------------------------------
// Public status API
// ---------------------------------------------------------------------------

/** Subscribe to the auto-save "in flight" flag. Fires immediately with the
 *  current value, then on every change. Returns an unsubscribe fn. */
export function subscribeSyncStatus(cb: (syncing: boolean) => void): () => void {
  _statusSubscribers.add(cb);
  try {
    cb(_syncing);
  } catch {
    /* ignore */
  }
  return () => {
    _statusSubscribers.delete(cb);
  };
}

/** True while an auto-save is in flight. */
export function isSyncing(): boolean {
  return _syncing;
}

// ---------------------------------------------------------------------------
// Pause / resume — suspend auto-save around programmatic document writes
// (load, hydration, agent redraft insertion) so we never persist a half-written
// intermediate state or fight the writer.
// ---------------------------------------------------------------------------

export function pauseSync(): void {
  _paused = true;
  if (_pendingTimer !== null) {
    clearTimeout(_pendingTimer);
    _pendingTimer = null;
  }
  console.log("[sync] pauseSync — auto-save suspended");
}

/**
 * Resume auto-save after a pause. ``runId`` is accepted for backward
 * compatibility with the legacy re-seed contract; it is no longer needed (the
 * next real edit re-arms the watcher on its own). Any timer armed by a
 * selection change that fired during the pause window is discarded so a cycle
 * can't run against a still-settling document.
 */
export function resumeSync(_runId?: string | null): void {
  if (_pendingTimer !== null) {
    clearTimeout(_pendingTimer);
    _pendingTimer = null;
  }
  _touchedSections.clear();
  _paused = false;
  console.log("[sync] resumeSync — auto-save re-enabled");
}

// ---------------------------------------------------------------------------
// Auto-save cycle
// ---------------------------------------------------------------------------

/** Resolve a dotted section number to its module id, using the live spine the
 *  shell holds. ``null`` when the number isn't in the spine — the scoped save
 *  then skips it rather than falling back to a whole-document fetch. */
export type ResolveModuleId = (sectionNumber: string) => string | null;

/** Save every section a REAL edit touched to the SSOT. Best-effort: a failure
 *  logs and leaves the section marked so the next settle (or the Save button)
 *  retries.
 *
 *  This no longer resolves the cursor's section here. It used to fold the caret's
 *  section into the touched set unconditionally, so a caret merely sitting in a
 *  section — armed by the 15 s selection heartbeat, with NO edit — "auto-saved"
 *  every tick, and each of those saves fetched all 90 sections. Sections are now
 *  recorded only by a paragraph (content) event at arm time; with nothing touched
 *  this returns before any network call. */
async function _runAutoSave(getRunId: () => string | null, resolveModuleId: ResolveModuleId | null): Promise<void> {
  if (_paused) return;
  if (_touchedSections.size === 0) return;
  const runId = getRunId();
  if (!runId) return;

  const sections = Array.from(_touchedSections);
  _touchedSections.clear();

  _setSyncing(true);
  try {
    const { captureAndSaveOneSection } = await import("../variant-c/services/writeBackBridge");
    for (const sectionNumber of sections) {
      if (_paused) {
        // A programmatic write started mid-save — requeue the rest and bail.
        _touchedSections.add(sectionNumber);
        continue;
      }
      const moduleId = resolveModuleId ? resolveModuleId(sectionNumber) : null;
      if (!moduleId) {
        // Not in the live spine (e.g. a heading the run doesn't own) — nothing
        // scoped to save; never widen to /all-sections to find out.
        console.warn(`[sync] auto-save skipped: no module id for section ${sectionNumber}`);
        continue;
      }
      try {
        await captureAndSaveOneSection(runId, sectionNumber, moduleId);
      } catch (err) {
        // Requeue so the next settle retries this section.
        _touchedSections.add(sectionNumber);
        console.warn(`[sync] auto-save failed for section ${sectionNumber}:`, err);
      }
    }
  } finally {
    _setSyncing(false);
  }
}

/** True when the running Word host supports WordApi 1.6 paragraph events
 *  (onParagraphChanged/Added/Deleted). Guarded — false on any uncertainty so
 *  the watcher falls back to selection-only triggering. */
function _wordApi16Supported(): boolean {
  try {
    const reqs = (
      globalThis as {
        Office?: { context?: { requirements?: { isSetSupported?: (n: string, v: string) => boolean } } };
      }
    ).Office?.context?.requirements;
    return typeof reqs?.isSetSupported === "function" && reqs.isSetSupported("WordApi", "1.6") === true;
  } catch {
    return false;
  }
}

/**
 * Start the auto-save watcher for the current run. The caller (VariantCShell)
 * gates this on the same lifecycle conditions the legacy engine used —
 * ``isSyncReady(runId)`` and ``draft_pointer !== "server"`` — so the watcher
 * never fires on placeholder scaffolding or while the server owns the doc.
 * Returns a cleanup fn that removes all handlers.
 */
export function initSyncEngine(
  getRunId: () => string | null = () => null,
  resolveModuleId: ResolveModuleId | null = null,
): () => void {
  console.log(`[sync] initSyncEngine — runId=${getRunId() ?? "none"}`);

  let eventsActive = _wordApi16Supported();

  // Only a REAL content edit (a paragraph event) may mark the cursor's section
  // as touched. A selection trigger — including the 15 s heartbeat — only flushes
  // sections an edit already touched; it never adds one, so an unedited document
  // produces no auto-save cycle at all. (The heartbeat's old "backstop for
  // toolbar formatting" role is moot: Save is markdown-only, so formatting never
  // changes the SSOT.) Recorded at arm time, before the debounce, so the
  // section is the one the edit actually landed in.
  const recordCursorSection = () => {
    void import("./wordWriter")
      .then(({ detectCursorSectionLight }) => detectCursorSectionLight())
      .then((section) => {
        if (section) _touchedSections.add(section);
      })
      .catch((err: unknown) => {
        console.warn("[sync] cursor-section resolve failed (non-fatal):", err);
      });
  };

  const armCycle = (trigger: SyncTrigger) => {
    if (_paused) return;

    // Throttle caret-only selection triggers to a heartbeat while paragraph
    // events are active — real edits already fire paragraph events.
    if (trigger === "selection" && eventsActive) {
      const now = _nowMs();
      if (now - _lastSelectionArmAt < SELECTION_HEARTBEAT_MS) return;
      _lastSelectionArmAt = now;
    }

    if (trigger === "paragraph") recordCursorSection();

    if (_pendingTimer !== null) clearTimeout(_pendingTimer);
    _pendingTimer = setTimeout(() => {
      _pendingTimer = null;
      void _runAutoSave(getRunId, resolveModuleId).catch((err: unknown) => {
        console.error("[sync] auto-save cycle exception:", String(err));
      });
    }, DEBOUNCE_MS);
  };

  const selectionHandler = () => armCycle("selection");
  Office.context.document.addHandlerAsync(Office.EventType.DocumentSelectionChanged, selectionHandler);
  console.log("[sync] handler registered on DocumentSelectionChanged");

  // WordApi 1.6: paragraph content events fire only on real edits, so typing
  // triggers an immediate section save while caret-only moves cost nothing.
  // Best-effort — any failure flips eventsActive=false so selection triggers
  // arm every change again (legacy fallback). Handlers die with the task pane;
  // cleanup below removes them on an explicit unmount.
  const paragraphEventRemovers: Array<() => void> = [];
  let paragraphEventContext: Word.RequestContext | null = null;
  if (eventsActive) {
    void Word.run(async (context) => {
      const doc = context.document;
      const changed = doc.onParagraphChanged.add(() => {
        armCycle("paragraph");
        return Promise.resolve();
      });
      const added = doc.onParagraphAdded.add(() => {
        armCycle("paragraph");
        return Promise.resolve();
      });
      const deleted = doc.onParagraphDeleted.add(() => {
        armCycle("paragraph");
        return Promise.resolve();
      });
      await context.sync();
      paragraphEventContext = context;
      paragraphEventRemovers.push(
        () => changed.remove(),
        () => added.remove(),
        () => deleted.remove(),
      );
      console.log("[sync] paragraph events registered (WordApi 1.6)");
    }).catch((err: unknown) => {
      eventsActive = false;
      console.warn("[sync] paragraph event registration failed — selection-only fallback", String(err));
    });
  } else {
    console.log("[sync] WordApi 1.6 unavailable — selection-only triggering");
  }

  return () => {
    if (_pendingTimer !== null) {
      clearTimeout(_pendingTimer);
      _pendingTimer = null;
    }
    Office.context.document.removeHandlerAsync(Office.EventType.DocumentSelectionChanged, {
      handler: selectionHandler,
    });
    if (paragraphEventRemovers.length > 0 && paragraphEventContext) {
      for (const remove of paragraphEventRemovers) remove();
      void paragraphEventContext.sync().catch(() => {
        /* handlers die with the task pane anyway */
      });
    }
    console.log("[sync] cleanup — handlers removed");
  };
}
